import { z } from "zod";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { expenseDocKindSchema, expensePaymentMethodSchema } from "../expenses/expenses.js";
/**
 * Varlık bakımı sözleşmeleri (Prompt 23, ADR 0023 §1–8): varlık listesi / kartı, varlık belgeleri, elle sayaç okuması,
 * bakım planları, iş emirleri (durum eylemleri, maliyet, fotoğraf, gider), ayarlar ve bakım raporu.
 */
// ── Sabitler ──────────────────────────────────────────────────────────────────
export const ASSET_KINDS = ["TANK", "DISPENSER", "DEVICE", "EV_CHARGER", "WASH_UNIT"];
export const assetKindSchema = z.enum(ASSET_KINDS);
export const ASSET_KIND_LABELS = {
    TANK: "Tank",
    DISPENSER: "Pompa",
    DEVICE: "Cihaz / ekipman",
    EV_CHARGER: "Şarj ünitesi",
    WASH_UNIT: "Yıkama ünitesi",
};
export const PLAN_TRIGGERS = ["INTERVAL_DAYS", "OPERATING_HOURS", "USAGE_COUNT"];
export const planTriggerSchema = z.enum(PLAN_TRIGGERS);
export const PLAN_TRIGGER_LABELS = {
    INTERVAL_DAYS: "Her N günde",
    OPERATING_HOURS: "Her N çalışma saatinde",
    USAGE_COUNT: "Her N adette",
};
export const USAGE_SOURCES = [
    "WASH_CYCLES",
    "EV_CHARGING_HOURS",
    "EV_SESSIONS",
    "PUMP_TRANSACTIONS",
    "MANUAL_HOURS",
    "MANUAL_COUNT",
];
export const usageSourceSchema = z.enum(USAGE_SOURCES);
export const USAGE_SOURCE_LABELS = {
    WASH_CYCLES: "Yıkama çalışması (adet)",
    EV_CHARGING_HOURS: "Şarj süresi (saat)",
    EV_SESSIONS: "Şarj oturumu (adet)",
    PUMP_TRANSACTIONS: "Pompa işlemi (adet)",
    MANUAL_HOURS: "Elle sayaç (saat)",
    MANUAL_COUNT: "Elle sayaç (adet)",
};
/**
 * Türe göre kullanılabilir kullanım kaynakları (ADR 0023 §3): otomatik ölçü diğer modüllerin tablolarından OKUNUR; her türde
 * elle sayaç da seçilebilir.
 */
export const USAGE_SOURCES_BY_KIND = {
    TANK: { OPERATING_HOURS: ["MANUAL_HOURS"], USAGE_COUNT: ["MANUAL_COUNT"] },
    DISPENSER: { OPERATING_HOURS: ["MANUAL_HOURS"], USAGE_COUNT: ["PUMP_TRANSACTIONS", "MANUAL_COUNT"] },
    DEVICE: { OPERATING_HOURS: ["MANUAL_HOURS"], USAGE_COUNT: ["MANUAL_COUNT"] },
    EV_CHARGER: {
        OPERATING_HOURS: ["EV_CHARGING_HOURS", "MANUAL_HOURS"],
        USAGE_COUNT: ["EV_SESSIONS", "MANUAL_COUNT"],
    },
    WASH_UNIT: { OPERATING_HOURS: ["MANUAL_HOURS"], USAGE_COUNT: ["WASH_CYCLES", "MANUAL_COUNT"] },
};
export const WORK_ORDER_TYPES = ["CORRECTIVE", "PREVENTIVE", "INSPECTION"];
export const workOrderTypeSchema = z.enum(WORK_ORDER_TYPES);
export const WORK_ORDER_TYPE_LABELS = {
    CORRECTIVE: "Arıza / onarım",
    PREVENTIVE: "Önleyici bakım",
    INSPECTION: "Kontrol",
};
export const WORK_ORDER_SOURCES = ["MANUAL", "PLAN", "ALARM"];
export const workOrderSourceSchema = z.enum(WORK_ORDER_SOURCES);
export const WORK_ORDER_SOURCE_LABELS = {
    MANUAL: "Elle",
    PLAN: "Bakım planı",
    ALARM: "Alarm",
};
export const WORK_ORDER_PRIORITIES = ["LOW", "NORMAL", "HIGH", "URGENT"];
export const workOrderPrioritySchema = z.enum(WORK_ORDER_PRIORITIES);
export const WORK_ORDER_PRIORITY_LABELS = {
    LOW: "Düşük",
    NORMAL: "Normal",
    HIGH: "Yüksek",
    URGENT: "Acil",
};
export const WORK_ORDER_STATUSES = [
    "OPEN",
    "ASSIGNED",
    "IN_PROGRESS",
    "ON_HOLD",
    "DONE",
    "CANCELLED",
];
export const workOrderStatusSchema = z.enum(WORK_ORDER_STATUSES);
export const WORK_ORDER_STATUS_LABELS = {
    OPEN: "Açık",
    ASSIGNED: "Atandı",
    IN_PROGRESS: "Çalışılıyor",
    ON_HOLD: "Beklemede",
    DONE: "Tamamlandı",
    CANCELLED: "İptal",
};
export const COST_KINDS = ["PART", "LABOR", "OTHER"];
export const costKindSchema = z.enum(COST_KINDS);
export const COST_KIND_LABELS = {
    PART: "Parça",
    LABOR: "İşçilik",
    OTHER: "Diğer",
};
export const ASSET_DOCUMENT_KINDS = [
    "INVOICE",
    "WARRANTY",
    "MANUAL",
    "CERTIFICATE",
    "SERVICE_REPORT",
    "OTHER",
];
export const assetDocumentKindSchema = z.enum(ASSET_DOCUMENT_KINDS);
export const ASSET_DOCUMENT_KIND_LABELS = {
    INVOICE: "Fatura",
    WARRANTY: "Garanti belgesi",
    MANUAL: "Kullanım kılavuzu",
    CERTIFICATE: "Sertifika",
    SERVICE_REPORT: "Servis raporu",
    OTHER: "Diğer",
};
export const METERS = ["HOURS", "COUNT"];
export const meterSchema = z.enum(METERS);
export const WORK_ORDER_ACTIONS = [
    "ASSIGN",
    "START",
    "HOLD",
    "RESUME",
    "COMPLETE",
    "CANCEL",
    "NOTE",
    "ADD_COST",
    "ADD_PHOTO",
    "CREATE_EXPENSE",
];
export const workOrderActionSchema = z.enum(WORK_ORDER_ACTIONS);
const positiveQuantity = quantitySchema.refine((value) => Number(value) > 0, "Sıfırdan büyük olmalıdır");
const nonNegativeQuantity = quantitySchema.refine((value) => Number(value) >= 0, "Negatif olamaz");
const nonNegativeUnitPrice = unitPriceSchema.refine((value) => !value.startsWith("-"), "Negatif olamaz");
const versionSchema = z.number().int().positive();
const noteSchema = z.string().trim().min(3, "En az 3 karakter").max(4000);
const refSchema = z.object({ id: uuidSchema, name: z.string() });
export const assetRefSchema = z.object({ assetKind: assetKindSchema, assetId: uuidSchema });
export const assetParamsSchema = z.object({ kind: assetKindSchema, id: uuidSchema });
// ── Varlıklar ─────────────────────────────────────────────────────────────────
export const maintenanceAssetSchema = z.object({
    assetKind: assetKindSchema,
    assetId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    name: z.string(),
    deviceType: z.string().nullable(),
    status: z.string(),
    serialNo: z.string().nullable(),
    installedOn: localDateSchema.nullable(),
    warrantyEndsOn: localDateSchema.nullable(),
    underWarranty: z.boolean(),
    serviceCounterparty: refSchema.nullable(),
    openWorkOrders: z.number().int().nonnegative(),
    activePlans: z.number().int().nonnegative(),
    /** Etkin planların en yakın vadesi (gün planları) — yoksa null. */
    nextPlanDueOn: localDateSchema.nullable(),
    documentCount: z.number().int().nonnegative(),
});
export const maintenanceAssetListQuerySchema = z.object({
    stationId: uuidSchema.optional(),
    companyId: uuidSchema.optional(),
    kind: assetKindSchema.optional(),
    search: z.string().trim().max(80).optional(),
    includeInactive: z.enum(["true", "false"]).optional(),
});
export const maintenanceAssetListSchema = z.object({ items: z.array(maintenanceAssetSchema) });
export const assetDocumentSchema = z.object({
    id: uuidSchema,
    docKind: assetDocumentKindSchema,
    title: z.string(),
    fileId: uuidSchema,
    fileName: z.string(),
    createdAt: timestampSchema,
    createdByName: z.string().nullable(),
    archivedAt: timestampSchema.nullable(),
    version: z.number().int(),
});
export const createAssetDocumentSchema = z.object({
    fileId: uuidSchema,
    docKind: assetDocumentKindSchema,
    title: z.string().trim().min(1, "Başlık zorunludur").max(160),
});
export const assetDocumentParamsSchema = z.object({ documentId: uuidSchema });
export const archiveAssetDocumentSchema = z.object({ version: versionSchema });
export const meterReadingSchema = z.object({
    id: uuidSchema,
    meter: meterSchema,
    value: quantitySchema,
    isReset: z.boolean(),
    readAt: timestampSchema,
    note: z.string().nullable(),
    recordedByName: z.string().nullable(),
});
export const createMeterReadingSchema = z.object({
    meter: meterSchema,
    value: nonNegativeQuantity,
    readAt: timestampSchema.optional().describe("Boşsa şimdi"),
    isReset: z.boolean().default(false).describe("Sayaç değişti / sıfırlandı (önceki okumalarla fark alınmaz)"),
    note: z.string().trim().max(300).nullable().default(null),
});
/** Kaynaktan okunan kullanım (son 30 gün ve toplam) — kartta bilgi amaçlı. */
export const assetUsageSchema = z.object({
    source: usageSourceSchema,
    last30Days: quantitySchema,
});
export const maintenanceAssetDetailSchema = z.object({
    asset: maintenanceAssetSchema,
    brand: z.string().nullable(),
    model: z.string().nullable(),
    documents: z.array(assetDocumentSchema),
    plans: z.array(z.lazy(() => maintenancePlanSchema)),
    recentWorkOrders: z.array(z.lazy(() => workOrderSummarySchema)),
    meterReadings: z.array(meterReadingSchema).describe("En yeni 20 elle okuma"),
    usage: z.array(assetUsageSchema),
    canManage: z.boolean().describe("Varlık alanları / belgeleri (stations.assets.manage)"),
    canManagePlans: z.boolean(),
});
// ── Planlar ───────────────────────────────────────────────────────────────────
const planDueSchema = z.object({
    due: z.boolean(),
    dueOn: localDateSchema.nullable(),
    usage: quantitySchema.nullable(),
    remaining: quantitySchema.nullable(),
    progressPercent: z.string(),
});
export const maintenancePlanSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    assetKind: assetKindSchema,
    assetId: uuidSchema,
    assetName: z.string(),
    name: z.string(),
    trigger: planTriggerSchema,
    usageSource: usageSourceSchema.nullable(),
    intervalValue: quantitySchema,
    leadDays: z.number().int(),
    leadUsage: quantitySchema,
    startsOn: localDateSchema,
    priority: workOrderPrioritySchema,
    serviceCounterparty: refSchema.nullable(),
    instructions: z.string().nullable(),
    isActive: z.boolean(),
    lastCompletedAt: timestampSchema.nullable(),
    lastCompletedOn: localDateSchema.nullable(),
    due: planDueSchema,
    openWorkOrder: z
        .object({ id: uuidSchema, workOrderNo: z.string(), status: workOrderStatusSchema })
        .nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
const planFields = {
    name: z.string().trim().min(1, "Plan adı zorunludur").max(120),
    trigger: planTriggerSchema,
    usageSource: usageSourceSchema.nullable().default(null),
    intervalValue: positiveQuantity,
    leadDays: z.number().int().min(0).max(365).default(7),
    leadUsage: nonNegativeQuantity.default("0"),
    startsOn: localDateSchema,
    priority: workOrderPrioritySchema.default("NORMAL"),
    serviceCounterpartyId: uuidSchema.nullable().default(null),
    instructions: z.string().trim().max(4000).nullable().default(null),
};
const planRules = (value, context) => {
    if (value.trigger === "INTERVAL_DAYS" && value.usageSource)
        context.addIssue({
            code: "custom",
            path: ["usageSource"],
            message: "Gün planında kullanım kaynağı seçilmez",
        });
    if (value.trigger && value.trigger !== "INTERVAL_DAYS" && !value.usageSource)
        context.addIssue({ code: "custom", path: ["usageSource"], message: "Kullanım kaynağı seçin" });
    if (value.trigger === "INTERVAL_DAYS" && value.intervalValue && !/^\d+(\.0+)?$/.test(value.intervalValue))
        context.addIssue({ code: "custom", path: ["intervalValue"], message: "Gün aralığı tam sayı olmalıdır" });
};
export const createMaintenancePlanSchema = z
    .object({ assetKind: assetKindSchema, assetId: uuidSchema, ...planFields })
    .superRefine(planRules);
export const updateMaintenancePlanSchema = z
    .object({
    name: planFields.name.optional(),
    intervalValue: positiveQuantity.optional(),
    leadDays: z.number().int().min(0).max(365).optional(),
    leadUsage: nonNegativeQuantity.optional(),
    priority: workOrderPrioritySchema.optional(),
    serviceCounterpartyId: uuidSchema.nullable().optional(),
    instructions: z.string().trim().max(4000).nullable().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
})
    .describe("Tetik ve kullanım kaynağı değiştirilemez (yeni plan açılır)");
export const maintenancePlanListQuerySchema = z.object({
    stationId: uuidSchema.optional(),
    assetKind: assetKindSchema.optional(),
    assetId: uuidSchema.optional(),
    active: z.enum(["true", "false"]).optional(),
    due: z.enum(["true"]).optional().describe("Yalnızca vadesi gelenler"),
});
export const maintenancePlanListSchema = z.object({ items: z.array(maintenancePlanSchema) });
export const evaluatePlansResultSchema = z.object({
    evaluated: z.number().int(),
    created: z.array(z.object({ planId: uuidSchema, workOrderId: uuidSchema, workOrderNo: z.string() })),
});
// ── İş emirleri ───────────────────────────────────────────────────────────────
const slaSchema = z.object({ responseBreached: z.boolean(), resolveBreached: z.boolean() });
export const workOrderSummarySchema = z.object({
    id: uuidSchema,
    workOrderNo: z.string(),
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    asset: z.object({ kind: assetKindSchema, id: uuidSchema, name: z.string() }).nullable(),
    workType: workOrderTypeSchema,
    source: workOrderSourceSchema,
    title: z.string(),
    priority: workOrderPrioritySchema,
    status: workOrderStatusSchema,
    createdAt: timestampSchema,
    responseDueAt: timestampSchema,
    resolveDueAt: timestampSchema,
    completedAt: timestampSchema.nullable(),
    sla: slaSchema,
    serviceCounterparty: refSchema.nullable(),
    assignedUser: refSchema.nullable(),
    totalCost: moneySchema.describe("KDV hariç maliyet toplamı (kuruş)"),
});
export const workOrderCostSchema = z.object({
    id: uuidSchema,
    kind: costKindSchema,
    description: z.string(),
    quantity: quantitySchema,
    unitPrice: z.string(),
    amount: moneySchema,
    vatRateCode: z.string(),
    createdAt: timestampSchema,
});
export const workOrderEventSchema = z.object({
    id: uuidSchema,
    kind: z.string(),
    fromStatus: workOrderStatusSchema.nullable(),
    toStatus: workOrderStatusSchema.nullable(),
    note: z.string().nullable(),
    data: z.record(z.string(), z.unknown()),
    actorName: z.string().nullable(),
    occurredAt: timestampSchema,
});
export const workOrderPhotoSchema = z.object({
    id: uuidSchema,
    fileId: uuidSchema,
    caption: z.string().nullable(),
    createdAt: timestampSchema,
});
export const workOrderDetailSchema = workOrderSummarySchema.extend({
    companyId: uuidSchema,
    description: z.string().nullable(),
    plan: refSchema.nullable(),
    alarm: z.object({ id: uuidSchema, title: z.string(), status: z.string() }).nullable(),
    dueOn: localDateSchema.nullable(),
    respondedAt: timestampSchema.nullable(),
    startedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    completionMeter: quantitySchema.nullable(),
    /** Plan elle sayaçlıysa tamamlarken sayaç değeri istenir. */
    completionMeterRequired: z.boolean(),
    costs: z.array(workOrderCostSchema),
    costTotals: z.object({
        parts: moneySchema,
        labor: moneySchema,
        other: moneySchema,
        net: moneySchema,
        vatRateCode: z.string().nullable(),
    }),
    photos: z.array(workOrderPhotoSchema),
    events: z.array(workOrderEventSchema),
    expense: z.object({ id: uuidSchema, expenseNo: z.string().nullable(), status: z.string() }).nullable(),
    allowedActions: z.array(workOrderActionSchema),
    version: z.number().int(),
});
export const createWorkOrderSchema = z
    .object({
    stationId: uuidSchema,
    assetKind: assetKindSchema.nullable().default(null),
    assetId: uuidSchema.nullable().default(null),
    workType: workOrderTypeSchema.default("CORRECTIVE"),
    title: z.string().trim().min(1, "Başlık zorunludur").max(200),
    description: z.string().trim().max(4000).nullable().default(null),
    priority: workOrderPrioritySchema.default("NORMAL"),
    serviceCounterpartyId: uuidSchema.nullable().default(null),
    assignedUserId: uuidSchema.nullable().default(null),
})
    .superRefine((value, context) => {
    if ((value.assetKind === null) !== (value.assetId === null))
        context.addIssue({
            code: "custom",
            path: ["assetId"],
            message: "Varlık türü ve varlık birlikte seçilmeli",
        });
});
export const workOrderListQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema.optional(),
    status: z.string().optional().describe('Virgülle ayrılmış durumlar ya da "OPEN_ALL" (tüm açık durumlar)'),
    priority: workOrderPrioritySchema.optional(),
    source: workOrderSourceSchema.optional(),
    workType: workOrderTypeSchema.optional(),
    assetKind: assetKindSchema.optional(),
    assetId: uuidSchema.optional(),
    slaBreached: z.enum(["true"]).optional(),
    search: z.string().trim().max(80).optional(),
});
export const workOrderListSchema = pageOf(workOrderSummarySchema);
export const workOrderParamsSchema = z.object({ id: uuidSchema });
export const workOrderCostParamsSchema = z.object({ id: uuidSchema, costId: uuidSchema });
export const assignWorkOrderSchema = z.object({
    serviceCounterpartyId: uuidSchema.nullable(),
    assignedUserId: uuidSchema.nullable(),
    version: versionSchema,
});
export const workOrderVersionSchema = z.object({ version: versionSchema });
export const holdWorkOrderSchema = z.object({ note: noteSchema, version: versionSchema });
export const completeWorkOrderSchema = z.object({
    resolutionNote: noteSchema,
    completionMeter: nonNegativeQuantity.nullable().default(null),
    version: versionSchema,
});
export const cancelWorkOrderSchema = z.object({
    reason: z.string().trim().min(3).max(500),
    version: versionSchema,
});
export const workOrderNoteSchema = z.object({ note: noteSchema });
export const addWorkOrderCostSchema = z.object({
    kind: costKindSchema,
    description: z.string().trim().min(1, "Açıklama zorunludur").max(200),
    quantity: positiveQuantity,
    unitPrice: nonNegativeUnitPrice.describe("KDV hariç birim fiyat (lira)"),
    vatRateCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
});
export const addWorkOrderPhotoSchema = z.object({
    fileId: uuidSchema,
    caption: z.string().trim().max(200).nullable().default(null),
});
export const createWorkOrderExpenseSchema = z.object({
    docKind: expenseDocKindSchema.default("INCOMING_INVOICE"),
    docNo: z.string().trim().max(60).nullable().default(null),
    docDate: localDateSchema.optional().describe("Boşsa bugün"),
    paymentMethod: expensePaymentMethodSchema
        .default("ON_ACCOUNT")
        .describe("Cariye borç: iş emrinin servis firması"),
    cashRegisterId: uuidSchema.nullable().default(null),
    bankAccountId: uuidSchema.nullable().default(null),
    companyCardId: uuidSchema.nullable().default(null),
    fileId: uuidSchema.nullable().default(null),
});
/** Atanabilir personel: istasyonda `maintenance.work.manage` izni olan etkin üyeler. */
export const assigneeCandidatesQuerySchema = z.object({ stationId: uuidSchema });
export const personCandidatesSchema = z.object({ items: z.array(refSchema) });
// ── Ayarlar ───────────────────────────────────────────────────────────────────
const slaHoursSchema = z.object({
    responseHours: z.number().int().min(1).max(2000),
    resolveHours: z.number().int().min(1).max(8760),
});
export const maintenanceSettingsSchema = z.object({
    sla: z.record(workOrderPrioritySchema, slaHoursSchema),
    autoWorkOrderFromAlarms: z.boolean(),
    expenseCategoryId: uuidSchema.nullable(),
    expenseCategoryName: z.string().nullable(),
    updatedAt: timestampSchema.nullable(),
    version: z.number().int().nonnegative(),
});
export const saveMaintenanceSettingsSchema = z
    .object({
    sla: z.record(workOrderPrioritySchema, slaHoursSchema),
    autoWorkOrderFromAlarms: z.boolean(),
    expenseCategoryId: uuidSchema.nullable(),
    version: z.number().int().nonnegative(),
})
    .superRefine((value, context) => {
    for (const priority of WORK_ORDER_PRIORITIES) {
        const hours = value.sla[priority];
        if (!hours)
            context.addIssue({ code: "custom", path: ["sla", priority], message: "Her öncelik için SLA girin" });
        else if (hours.resolveHours < hours.responseHours)
            context.addIssue({
                code: "custom",
                path: ["sla", priority, "resolveHours"],
                message: "Çözüm süresi yanıt süresinden kısa olamaz",
            });
    }
});
// ── Rapor ─────────────────────────────────────────────────────────────────────
export const maintenanceReportQuerySchema = z
    .object({
    from: localDateSchema,
    to: localDateSchema,
    stationId: uuidSchema.optional(),
    companyId: uuidSchema.optional(),
    assetKind: assetKindSchema.optional(),
})
    .refine((value) => value.from <= value.to, {
    message: "Başlangıç tarihi bitişten sonra olamaz",
    path: ["from"],
});
const reportFigures = {
    failures: z.number().int().nonnegative().describe("Arıza iş emri (CORRECTIVE)"),
    preventiveDone: z.number().int().nonnegative(),
    openWorkOrders: z.number().int().nonnegative(),
    slaBreaches: z.number().int().nonnegative(),
    mttrHours: z.string().nullable().describe("Ortalama onarım süresi (saat, 1 ondalık)"),
    parts: moneySchema,
    labor: moneySchema,
    other: moneySchema,
    total: moneySchema,
};
export const maintenanceReportSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    rows: z.array(z.object({
        stationId: uuidSchema,
        stationCode: z.string(),
        assetKind: assetKindSchema.nullable(),
        assetId: uuidSchema.nullable(),
        assetName: z.string().describe('Varlıksız iş emirleri "İstasyon geneli"'),
        ...reportFigures,
    })),
    totals: z.object(reportFigures),
});
//# sourceMappingURL=api.js.map